// ============================================
// CONSULTANT PERMISSIONS MANAGER
// ============================================
// CF Admin interface for managing consultant access to client AI context
// - Assign consultants to companies
// - Toggle AI context access per consultant/company
// - Set temporary access with expiration
// - Bulk operations

import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Badge } from "./ui/badge";
import { Alert, AlertDescription } from "./ui/alert";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Users, Shield, ShieldCheck, ShieldAlert, Calendar, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

interface ConsultantPermission {
  consultantId: string;
  consultantName: string;
  consultantEmail: string;
  companyId: string;
  companyName: string;
  firmId: string;
  hasAccess: boolean;
  aiContextEnabled: boolean;
  grantedBy: string;
  grantedByName: string;
  grantedAt: string;
  expiresAt?: string;
  notes?: string;
}

interface Consultant {
  id: string;
  name: string;
  email: string;
}

interface Company {
  id: string;
  name: string;
}

interface ConsultantPermissionsManagerProps {
  firmId: string;
  accessToken: string;
  projectId: string;
  publicAnonKey: string;
}

export function ConsultantPermissionsManager({
  firmId,
  accessToken,
  projectId,
  publicAnonKey,
}: ConsultantPermissionsManagerProps) {
  const [permissions, setPermissions] = useState<ConsultantPermission[]>([]);
  const [consultants, setConsultants] = useState<Consultant[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterConsultant, setFilterConsultant] = useState<string>("all");
  const [filterCompany, setFilterCompany] = useState<string>("all");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedConsultant, setSelectedConsultant] = useState<string>("");
  const [selectedCompany, setSelectedCompany] = useState<string>("");
  const [aiContextEnabled, setAiContextEnabled] = useState(true);

  useEffect(() => {
    loadData();
  }, [firmId]);

  async function loadData() {
    try {
      setLoading(true);

      // Load permissions
      const permResponse = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/ai/permissions/${firmId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!permResponse.ok) {
        throw new Error("Failed to load permissions");
      }

      const permData = await permResponse.json();
      setPermissions(permData.permissions || []);

      // The firm's members (GET /firms/:firmId/users) are the consultants who can be granted access.
      const consultantsResponse = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firms/${firmId}/users`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      if (consultantsResponse.ok) {
        const consultantsData = await consultantsResponse.json();
        setConsultants((consultantsData.users || []).map((u: any) => ({ id: u.id, name: u.name || u.email, email: u.email })));
      }

      // The firm's clients (GET /consultant/companies) are the companies access is granted on.
      const companiesResponse = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consultant/companies`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      if (companiesResponse.ok) {
        const companiesData = await companiesResponse.json();
        const list = Array.isArray(companiesData) ? companiesData : companiesData.companies || [];
        setCompanies(list.map((co: any) => ({ id: co.id, name: co.name || co.companyName || co.id })));
      }
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Failed to load permissions data");
    } finally {
      setLoading(false);
    }
  }

  async function toggleAIContext(consultantId: string, companyId: string, currentState: boolean) {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/ai/permissions/${firmId}/${consultantId}/${companyId}/toggle-ai-context`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ enabled: !currentState }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to toggle AI context");
      }

      toast.success(
        `AI context ${!currentState ? "enabled" : "disabled"} successfully`
      );
      await loadData();
    } catch (error) {
      console.error("Error toggling AI context:", error);
      toast.error("Failed to toggle AI context access");
    }
  }

  async function addPermission() {
    if (!selectedConsultant || !selectedCompany) {
      toast.error("Please select both consultant and company");
      return;
    }

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/ai/permissions/${firmId}/${selectedConsultant}/${selectedCompany}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            hasAccess: true,
            aiContextEnabled,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to add permission");
      }

      toast.success("Consultant access granted successfully");
      setShowAddDialog(false);
      setSelectedConsultant("");
      setSelectedCompany("");
      setAiContextEnabled(true);
      await loadData();
    } catch (error) {
      console.error("Error adding permission:", error);
      toast.error("Failed to grant access");
    }
  }

  async function revokeAccess(consultantId: string, companyId: string) {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/ai/permissions/${firmId}/${consultantId}/${companyId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to revoke access");
      }

      toast.success("Access revoked successfully");
      await loadData();
    } catch (error) {
      console.error("Error revoking access:", error);
      toast.error("Failed to revoke access");
    }
  }

  const filteredPermissions = permissions.filter((perm) => {
    if (filterConsultant !== "all" && perm.consultantId !== filterConsultant) {
      return false;
    }
    if (filterCompany !== "all" && perm.companyId !== filterCompany) {
      return false;
    }
    return true;
  });

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Consultant AI Context Permissions
              </CardTitle>
              <CardDescription>
                Manage which consultants can access AI context for each client engagement
              </CardDescription>
            </div>
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Grant Access
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Grant Consultant Access</DialogTitle>
                  <DialogDescription>
                    Assign a consultant to a company and configure AI context permissions
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Consultant</Label>
                    <Select value={selectedConsultant} onValueChange={setSelectedConsultant}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select consultant" />
                      </SelectTrigger>
                      <SelectContent>
                        {consultants.map((consultant) => (
                          <SelectItem key={consultant.id} value={consultant.id}>
                            {consultant.name} ({consultant.email})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Company</Label>
                    <Select value={selectedCompany} onValueChange={setSelectedCompany}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select company" />
                      </SelectTrigger>
                      <SelectContent>
                        {companies.map((company) => (
                          <SelectItem key={company.id} value={company.id}>
                            {company.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="space-y-0.5">
                      <Label>Enable AI Context Access</Label>
                      <p className="text-sm text-muted-foreground">
                        Allow AI to learn from this engagement
                      </p>
                    </div>
                    <Switch
                      checked={aiContextEnabled}
                      onCheckedChange={setAiContextEnabled}
                    />
                  </div>

                  <Alert>
                    <ShieldCheck className="h-4 w-4" />
                    <AlertDescription>
                      Each firm's client data is kept separate, and AI context records are encrypted. AI context helps
                      provide smarter recommendations based on this specific implementation.
                    </AlertDescription>
                  </Alert>
                </div>

                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                    Cancel
                  </Button>
                  <Button onClick={addPermission}>Grant Access</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Filters */}
            <div className="flex gap-4">
              <div className="flex-1">
                <Label>Filter by Consultant</Label>
                <Select value={filterConsultant} onValueChange={setFilterConsultant}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Consultants</SelectItem>
                    {consultants.map((consultant) => (
                      <SelectItem key={consultant.id} value={consultant.id}>
                        {consultant.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <Label>Filter by Company</Label>
                <Select value={filterCompany} onValueChange={setFilterCompany}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Companies</SelectItem>
                    {companies.map((company) => (
                      <SelectItem key={company.id} value={company.id}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Permissions Table */}
            {filteredPermissions.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-4 opacity-20" />
                <p>No permissions configured yet</p>
                <p className="text-sm mt-2">
                  Click "Grant Access" to assign consultants to client engagements
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Consultant</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Access Status</TableHead>
                    <TableHead>AI Context</TableHead>
                    <TableHead>Granted By</TableHead>
                    <TableHead>Granted Date</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPermissions.map((perm) => (
                    <TableRow key={`${perm.consultantId}-${perm.companyId}`}>
                      <TableCell>
                        <div>
                          <div>{perm.consultantName}</div>
                          <div className="text-sm text-muted-foreground">
                            {perm.consultantEmail}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{perm.companyName}</TableCell>
                      <TableCell>
                        {perm.hasAccess ? (
                          <Badge variant="default" className="gap-1">
                            <ShieldCheck className="h-3 w-3" />
                            Active
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="gap-1">
                            <ShieldAlert className="h-3 w-3" />
                            Inactive
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={perm.aiContextEnabled}
                          onCheckedChange={() =>
                            toggleAIContext(
                              perm.consultantId,
                              perm.companyId,
                              perm.aiContextEnabled
                            )
                          }
                          disabled={!perm.hasAccess}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{perm.grantedByName}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {new Date(perm.grantedAt).toLocaleDateString()}
                        </div>
                        {perm.expiresAt && (
                          <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                            <Calendar className="h-3 w-3" />
                            Expires: {new Date(perm.expiresAt).toLocaleDateString()}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => revokeAccess(perm.consultantId, perm.companyId)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Info Cards */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Total Assignments</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl">{permissions.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">AI Context Enabled</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl">
              {permissions.filter((p) => p.aiContextEnabled).length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Active Consultants</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl">
              {new Set(permissions.map((p) => p.consultantId)).size}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}